/**
 * AG Grid server-side datasource for the cross-space Space-local attributes
 * table, modeled on `VaultDocumentsGraphqlDataSource`
 * (`../Document/vaultDocumentsDataSource.ts`) — see that file's header for the
 * full rationale behind the block-index -> cursor walk this mirrors.
 *
 * `spaceIds` / `domains` / `nameFilter` / `valueTypes` / `qualityContracts`
 * are all derived per-request from the grid's own `filterModel` (DV-495) via
 * `spaceLocalFiltersFromFilterModel`, not held as constructor state — a
 * column filter change doesn't need a new datasource instance, since
 * `getRows` reads the filter model fresh every call. `lookups` (space/domain
 * name -> id/slug) is mutable via `setLookups` for the same reason: what
 * makes something constructor state here is whether changing it must
 * invalidate the cursor map, and a catalog rename doesn't.
 */
import { type ApolloClient } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { SpaceLocalAttributesSearchDocument } from "#graphql/supergraphHooks";
import {
  type SpaceLocalAttributesInput,
  type SpaceLocalAttributesSearchQuery,
  type SpaceLocalAttributesSearchQueryVariables,
} from "#graphql/supergraphTypes";
import { abandonRequest } from "../serverSideDatasource";
import {
  matchesNoRows,
  spaceLocalFiltersFromFilterModel,
  spaceLocalOrderByFromSortModel,
  type SpaceLocalFilterLookups,
} from "./spaceLocalAttributeFilterModel";
import type { SpaceLocalAttributeNode } from "./mappers";

type SpaceLocalAttributeConnection =
  SpaceLocalAttributesSearchQuery["spaceLocalAttributesSearch"];

export interface SpaceLocalAttributesDataSourceProps<TRow> {
  mapNode: (node: SpaceLocalAttributeNode) => TRow;
  /** Block size, kept in sync with the grid's `cacheBlockSize` -> `first`. */
  pageSize: number;
  /** Resolves the "Space" / "Domain" column filters' ticked display names to wire ids. */
  lookups: SpaceLocalFilterLookups;
  onTotalCountChange?: (totalCount: number) => void;
}

const buildInput = (
  pageSize: number,
  lookups: SpaceLocalFilterLookups,
  filterModel: AgGrid.FilterModel | null | undefined,
  sortModel: AgGrid.SortModelItem[] | null | undefined,
  after: string | null,
): SpaceLocalAttributesInput => {
  const { nameFilter, valueTypes, spaceIds, domains, qualityContracts } =
    spaceLocalFiltersFromFilterModel(filterModel, lookups);
  const orderBy = spaceLocalOrderByFromSortModel(sortModel);
  const input: SpaceLocalAttributesInput = { first: pageSize, orderBy };
  if (spaceIds?.length) input.spaceIds = spaceIds;
  if (domains?.length) input.domains = domains;
  if (nameFilter) input.nameFilter = nameFilter;
  if (valueTypes?.length) input.valueTypes = valueTypes;
  if (qualityContracts?.length) input.qualityContracts = qualityContracts;
  if (after != null) input.after = after;
  return input;
};

export class SpaceLocalAttributesGraphqlDataSource<TRow>
  implements AgGrid.IServerSideDatasource
{
  private client: ApolloClient<object>;
  private mapNode: (node: SpaceLocalAttributeNode) => TRow;
  private pageSize: number;
  private lookups: SpaceLocalFilterLookups;
  private onTotalCountChange?: (totalCount: number) => void;

  /** Block index -> the `after` cursor that fetches it (block 0 = null). */
  private blockCursors = new Map<number, string | null>();
  /** Serialized filter+sort of the last request, to detect resets. */
  private lastModelKey: string | null = null;
  /**
   * Bumped ONLY when the filter/sort model changes. In-flight responses from
   * an older generation are dropped. See `VaultDocumentsGraphqlDataSource` for
   * why concurrent blocks within the SAME generation are not dropped.
   */
  private generation = 0;

  constructor(client: ApolloClient<object>, props: SpaceLocalAttributesDataSourceProps<TRow>) {
    this.client = client;
    this.mapNode = props.mapNode;
    this.pageSize = props.pageSize;
    this.lookups = props.lookups;
    this.onTotalCountChange = props.onTotalCountChange;
    this.blockCursors.set(0, null);
  }

  /** Update the Space/Domain lookups without resetting cursors — a catalog rename shouldn't invalidate a page the user is already on. */
  setLookups(lookups: SpaceLocalFilterLookups): void {
    this.lookups = lookups;
  }

  private blockIndex(startRow: number): number {
    return Math.floor(startRow / this.pageSize);
  }

  private resetCursors(): void {
    this.blockCursors.clear();
    this.blockCursors.set(0, null);
  }

  /**
   * Force the next `getRows` to refetch from block 0 with fresh cursors. Pair
   * it with the grid's `refreshServerSide({ purge: true })`, mirroring
   * `VaultDocumentsGraphqlDataSource.refresh`.
   */
  refresh(): void {
    this.resetCursors();
    this.lastModelKey = null;
    this.generation += 1;
  }

  getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const startRow = params.request.startRow ?? 0;
    const blockIndex = this.blockIndex(startRow);
    const filterModel = params.request.filterModel as AgGrid.FilterModel;
    const sortModel = params.request.sortModel;

    const modelKey = JSON.stringify({ filterModel, sortModel });
    if (modelKey !== this.lastModelKey) {
      this.resetCursors();
      this.lastModelKey = modelKey;
      this.generation += 1;
    }

    void this.serveBlock(blockIndex, filterModel, sortModel, this.generation, params);
  }

  /**
   * Fetch one cursor page. Returns the connection, `null` on a missing/failed
   * response, or `undefined` when a newer generation has superseded this
   * request.
   */
  private async fetchBlock(
    filterModel: AgGrid.FilterModel | null | undefined,
    sortModel: AgGrid.SortModelItem[] | null | undefined,
    after: string | null,
    generation: number,
  ): Promise<SpaceLocalAttributeConnection | null | undefined> {
    const response = await this.client.query<
      SpaceLocalAttributesSearchQuery,
      SpaceLocalAttributesSearchQueryVariables
    >({
      query: SpaceLocalAttributesSearchDocument,
      variables: {
        input: buildInput(this.pageSize, this.lookups, filterModel, sortModel, after),
      },
      fetchPolicy: "no-cache",
    });
    if (generation !== this.generation) return undefined;
    return response.data?.spaceLocalAttributesSearch ?? null;
  }

  /**
   * Serve a block by absolute index, walking the forward cursor from the
   * deepest known block when the target's cursor hasn't been discovered yet.
   * See `VaultDocumentsGraphqlDataSource.serveBlock` for the full rationale —
   * this mirrors it exactly, just against `spaceLocalAttributesSearch`.
   */
  private async serveBlock(
    blockIndex: number,
    filterModel: AgGrid.FilterModel | null | undefined,
    sortModel: AgGrid.SortModelItem[] | null | undefined,
    generation: number,
    params: AgGrid.IServerSideGetRowsParams,
  ): Promise<void> {
    // A ticked-but-unresolvable set filter (Space/Domain/Requirement) means
    // "match nothing," not "no filter" — short-circuit rather than sending a
    // request that would come back with every row (see
    // `spaceLocalAttributeFilterModel.ts`'s `matchesNoRows`).
    if (matchesNoRows(spaceLocalFiltersFromFilterModel(filterModel, this.lookups))) {
      this.onTotalCountChange?.(0);
      params.success({ rowData: [], rowCount: 0 });
      params.api?.showNoRowsOverlay();
      return;
    }
    try {
      while (this.blockCursors.get(blockIndex) === undefined) {
        let probe = blockIndex - 1;
        while (probe > 0 && this.blockCursors.get(probe) === undefined) probe -= 1;

        const conn = await this.fetchBlock(
          filterModel,
          sortModel,
          this.blockCursors.get(probe) ?? null,
          generation,
        );
        if (conn === undefined) return void abandonRequest(params); // superseded
        if (!conn) return void params.fail();

        if (conn.pageInfo.hasNextPage && conn.pageInfo.endCursor != null) {
          this.blockCursors.set(probe + 1, conn.pageInfo.endCursor);
        } else {
          return void params.fail();
        }
      }

      const conn = await this.fetchBlock(
        filterModel,
        sortModel,
        this.blockCursors.get(blockIndex) ?? null,
        generation,
      );
      if (conn === undefined) return void abandonRequest(params); // superseded
      if (!conn) return void params.fail();

      const { nodes, pageInfo, totalCount } = conn;
      this.onTotalCountChange?.(totalCount);

      if (pageInfo.hasNextPage && pageInfo.endCursor != null) {
        this.blockCursors.set(blockIndex + 1, pageInfo.endCursor);
      }

      const rowData = nodes.map((n) => this.mapNode(n));
      params.success({ rowData, rowCount: totalCount });

      if (params.api) {
        if (totalCount === 0) params.api.showNoRowsOverlay();
        else params.api.hideOverlay();
      }
    } catch {
      params.fail();
    }
  }
}
